import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import styles from './ChatBot.module.scss';
import { ChatMessage } from './ChatMessage.tsx';
import { GeneratingIndicator } from './GeneratingIndicator.tsx';
import { TextArea } from 'components/uikit/inputs/TextArea.tsx';
import {
  TextButton,
  TextButtonColor,
} from 'components/uikit/buttons/TextButton.tsx';
import { Draggable } from 'components/uikit/draggable/Draggable.tsx';
import ArrowUpIcon from 'assets/icons/arrow_up_20.svg?react';
import { ChatPlaceholder } from './ChatPlaceholder.tsx';
import clsx from 'clsx';
import { useState, useEffect, useRef } from 'react';
import { QueryFactory } from 'services/api';
import { MessageModel } from './types/chatMessage.ts';
import RefreshIcon from 'assets/icons/refresh_24.svg?react';
import { BrightTooltip } from 'components/tooltips/BrightTooltip.tsx';
import { FeatureFlags } from 'application/constants/env-variables.ts';
import { showErrorToast } from 'components/uikit/toast/showToast.tsx';

export type ChatBotProps = {
  header: React.ReactNode;
  isOpen?: boolean;
  placeholder: {
    mainText: string | React.ReactNode;
    helperText?: string | React.ReactNode;
  };
  classes?: {
    chatContainer?: string;
    messageContainer?: string;
    promptInputFooter?: string;
    promptTextArea?: string;
  };
  sendInitialMessage?: () => Promise<MessageModel>;
};

export const ChatBot = (props: ChatBotProps) => {
  const { classes } = props;

  const i18n = useScopedTranslation('Component.ChatBot');

  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<MessageModel[]>([]);
  const [prompt, setPrompt] = useState<string>('');

  const messagesBottomRef = useRef<HTMLDivElement>(null);
  // Scrolling to the end of chat when new message or loading appears.
  useEffect(() => {
    messagesBottomRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  }, [messages, isLoading]);

  const initialMessageSentRef = useRef(false);
  useEffect(() => {
    if (
      !props.sendInitialMessage ||
      initialMessageSentRef.current ||
      !props.isOpen
    )
      return;

    initialMessageSentRef.current = true;

    const sendMessage = async () => {
      await withLoading(async () => {
        try {
          const message = await props.sendInitialMessage!();
          setMessages((prev) => [...prev, message]);
        } catch (e) {
          // todo: rework in task 78966
          // https://dev.azure.com/prismacode/magadan/_sprints/taskboard/magadan%20Team/magadan/Sprint%2042?workitem=78966
          showErrorToast(e);
        }
      });
    };

    sendMessage();
  }, [props.sendInitialMessage, props.isOpen]);

  useEffect(() => {
    if (!props.isOpen) initialMessageSentRef.current = false;
  }, [props.isOpen]);

  const sendMessage = async () => {
    const userMessage: MessageModel = { text: prompt, isLlmResponse: false };
    setMessages((prev) => [...prev, userMessage]);
    setPrompt('');

    const lastResponseId = [...messages]
      .reverse()
      .find((message) => message.isLlmResponse)?.responseId;

    await withLoading(async () => {
      try {
        const result = await QueryFactory.LLMClient.processChatMessage({
          chatMessage: { message: prompt, images: [] },
          previousResponseId: lastResponseId ?? null,
        });
        const messageModel: MessageModel = {
          text: result.response,
          responseId: result.responseId,
          isLlmResponse: true,
        };
        setMessages((prev) => [...prev, messageModel]);
      } catch (e) {
        // todo: rework in task 78966
        // https://dev.azure.com/prismacode/magadan/_sprints/taskboard/magadan%20Team/magadan/Sprint%2042?workitem=78966
        showErrorToast(e);
      }
    });
  };

  const withLoading = async (action: () => Promise<void>) => {
    setIsLoading(true);
    try {
      await action();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Draggable header={props.header} hidden={!props.isOpen}>
      <div className={clsx(styles.chatContainer, classes?.chatContainer)}>
        {messages.length > 0 || isLoading ? (
          <div
            className={clsx(
              styles.messagesContainer,
              classes?.messageContainer,
            )}
          >
            {messages.map((m) => (
              <ChatMessage
                message={m}
                isError={false}
                onSendAgain={() => {}}
                onDelete={() => {}}
              />
            ))}
            {isLoading && <GeneratingIndicator />}
            <div ref={messagesBottomRef} />
          </div>
        ) : (
          <ChatPlaceholder {...props.placeholder} />
        )}
        {FeatureFlags.aiChatEnabled() && (
          <div className={styles.resetButtonContainer}>
            <BrightTooltip title={i18n.t('reset_chat_tooltip')}>
              <TextButton
                icon={<RefreshIcon />}
                color={TextButtonColor.Primary}
                className={styles.resetButton}
                onClick={() => setMessages([])}
                disabled={!messages.length || isLoading}
              />
            </BrightTooltip>
          </div>
        )}
        <div
          className={clsx(styles.promptInputFooter, classes?.promptInputFooter)}
        >
          <TextArea
            maxRows={6}
            className={clsx(styles.promptTextArea, classes?.promptTextArea)}
            placeholder={i18n.t('input_placeholder')}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (prompt.length > 0) await sendMessage();
              }
            }}
          />
          <TextButton
            icon={<ArrowUpIcon />}
            className={styles.sendButton}
            onClick={sendMessage}
            disabled={prompt.length === 0}
          />
        </div>
      </div>
    </Draggable>
  );
};
